import {
    AwardIcon,
    BriefcaseIcon,
    Building2Icon,
    ClockIcon,
    HandshakeIcon,
    ListChecksIcon,
    ListIcon,
    ReceiptTextIcon,
    ScaleIcon,
    SearchIcon,
    type SvgIconComponent,
    TableGridIcon,
    TagIcon,
    UserIcon,
    UsersIcon,
} from 'components/icons';
import type { IntlShape } from 'react-intl';
import type { FigureDefinition, FigureRows, FigureRowsColumn } from 'types/aiChatFigure';
import { type FamilyId, familyTileClass, NEUTRAL_TILE_CLASS } from 'utils/families';

/** What a Figure's rows are, for its tile: the record type's family and glyph. No family is the neutral tile. */
interface Subject {
    family?: FamilyId;
    icon: SvgIconComponent;
}

const CASE: Subject = { family: 'cases', icon: ScaleIcon };
const CONTACT: Subject = { family: 'contacts', icon: UsersIcon };
const COLLEAGUE: Subject = { family: 'people', icon: UserIcon };
const INVOICE: Subject = { family: 'finance', icon: ReceiptTextIcon };
const EXPERIENCE: Subject = { family: 'bd', icon: AwardIcon };

/** Every search a Figure may read, with its rows' subject. Each names its rows by its step chip's noun. */
const SEARCH_SUBJECT: Partial<Record<string, Subject>> = {
    searchCases: CASE,
    semanticSearchCases: CASE,
    searchContacts: CONTACT,
    searchGroups: CONTACT,
    suggestClientsToContact: CONTACT,
    searchReferrals: { family: 'bd', icon: HandshakeIcon },
    searchEfforts: { family: 'bd', icon: HandshakeIcon },
    searchExperienceRecords: EXPERIENCE,
    semanticSearchExperienceRecords: EXPERIENCE,
    searchEmployees: COLLEAGUE,
    searchTeams: { family: 'people', icon: UsersIcon },
    searchOffices: { family: 'people', icon: Building2Icon },
    searchPracticeAreas: { family: 'people', icon: ScaleIcon },
    searchInvoices: INVOICE,
    searchInvoiceLines: INVOICE,
    searchPricelists: { family: 'finance', icon: TagIcon },
    searchTimecards: { icon: ClockIcon },
    searchTasks: { icon: ListChecksIcon },
    searchJobPositions: { icon: BriefcaseIcon },
    searchJobRoles: { icon: BriefcaseIcon },
    searchEverything: { icon: SearchIcon },
};

// Each grouping noun (`ai-chat.figure.group.<noun>`), the record its rows are when they are one, and the `groupBy`
// values that name it, lower-cased without underscores as the backend reads them, aliases included.
const GROUPING_NOUNS: [noun: string, subject: Subject | undefined, ...values: string[]][] = [
    ['case', CASE, 'case', 'matter'],
    ['client', CONTACT, 'client', 'clientparty'],
    ['payer', CONTACT, 'payer'],
    ['contact', CONTACT, 'contact'],
    ['employee', COLLEAGUE, 'employee', 'creatoremployee'],
    ['responsible-lawyer', COLLEAGUE, 'responsiblelawyer', 'responsibleemployee'],
    ['supervising-partner', COLLEAGUE, 'supervisingpartner', 'supervisedbyemployee'],
    ['core-team', undefined, 'coreteam', 'coreteams', 'team', 'teams'],
    ['practice-area', undefined, 'practicearea'],
    ['office', undefined, 'office', 'handledbyoffice'],
    ['year', undefined, 'year', 'openedyear', 'closedyear'],
    ['month', undefined, 'month', 'openedmonth', 'closedmonth'],
    ['week', undefined, 'week', 'weekstart'],
    ['day', undefined, 'day'],
    ['status', undefined, 'status', 'matterstatetypecode', 'timecardstatus'],
    ['case-type', undefined, 'casetype', 'mattertypecode'],
    ['activity-type', undefined, 'activitytype'],
    ['job-role', undefined, 'jobrole'],
    ['assignment', undefined, 'assignment'],
    ['language', undefined, 'language'],
    [
        'country',
        undefined,
        'handledofficecountry',
        'employeeofficecountry',
        'clientbillingcountry',
        'clientregistrationcountry',
    ],
    ['client-segment', undefined, 'clientsegment', 'segment'],
];

const GROUPING = new Map(
    GROUPING_NOUNS.flatMap(([noun, subject, ...values]) => values.map((value) => [value, { noun, subject }] as const))
);

const isAggregation = (tool: string) => tool.startsWith('aggregate');

/** The aggregation's `groupBy` values, one or a list, as the backend reads them. */
const groupingsOf = ({ source }: FigureDefinition): string[] => {
    const groupBy = source?.arguments?.groupBy;
    const values = Array.isArray(groupBy) ? groupBy : [groupBy];
    return values.flatMap((value) =>
        typeof value === 'string' && value.trim() ? [value.trim().replaceAll('_', '').toLowerCase()] : []
    );
};

const subjectOf = (definition: FigureDefinition): Subject | undefined => {
    const tool = definition.source?.tool;
    if (!tool) {
        return undefined;
    }
    if (!isAggregation(tool)) {
        return SEARCH_SUBJECT[tool];
    }
    const groupings = groupingsOf(definition);
    return groupings.length === 1 ? GROUPING.get(groupings[0])?.subject : undefined;
};

/** The Figure's tile: its rows' record type in its family's pair, else the neutral tile with the kind's glyph. */
export const figureTile = (definition: FigureDefinition): { icon: SvgIconComponent; className: string } => {
    const subject = subjectOf(definition);
    return subject
        ? { icon: subject.icon, className: familyTileClass(subject.family) }
        : { icon: definition.kind === 'list' ? ListIcon : TableGridIcon, className: NEUTRAL_TILE_CLASS };
};

/** Each row set's count message. A noun a search already counts by is that search's step chip message. */
const ROW_SET_NOUN_ID: Partial<Record<string, string>> = {
    rates: 'ai-chat.figure.rows.rate',
    personRates: 'ai-chat.figure.rows.rate',
    periodRates: 'ai-chat.figure.rows.rate',
    lines: 'ai-chat.progress.chip.searchInvoiceLines',
    lineTimecards: 'ai-chat.progress.chip.searchTimecards',
    members: 'ai-chat.figure.rows.member',
    primaryContacts: 'ai-chat.figure.rows.primary-contact',
};

/**
 * The message that counts the Figure's rows, an ICU plural on `count`: a row set's own noun ("12 rates"), a search's
 * step chip noun ("14 invoices"), an aggregation's grouping noun ("12 clients"; "groups" for several, "totals" for
 * none), plain rows otherwise. A row set this build does not know counts plain rows, never the source's noun.
 */
export const figureNounId = (definition: FigureDefinition): string => {
    if (definition.rows) {
        return ROW_SET_NOUN_ID[definition.rows] ?? 'ai-chat.table.row-count';
    }
    const tool = definition.source?.tool;
    if (tool && isAggregation(tool)) {
        const groupings = groupingsOf(definition);
        const noun = groupings.length === 1 ? (GROUPING.get(groupings[0])?.noun ?? 'several') : null;
        return `ai-chat.figure.group.${noun ?? (groupings.length ? 'several' : 'none')}`;
    }
    return tool && SEARCH_SUBJECT[tool] ? `ai-chat.progress.chip.${tool}` : 'ai-chat.table.row-count';
};

/** The noun alone, for "No {noun} match": the counted message for several rows, its number dropped. */
export const figureNoun = (intl: IntlShape, definition: FigureDefinition): string =>
    intl.formatMessage({ id: figureNounId(definition) }, { count: 2 }).replace(/^\S+\s+/, '');

/** "50 of 1,284 shown" for rows the record limit cut, "First 50 shown" when the source does not say how many matched. */
export const shownOfTotal = (intl: IntlShape, shown: number, total: number | null): string =>
    total === null
        ? intl.formatMessage({ id: 'ai-chat.tool-call.first-shown' }, { shown: intl.formatNumber(shown) })
        : intl.formatMessage(
              { id: 'ai-chat.tool-call.partial' },
              { shown: intl.formatNumber(shown), total: intl.formatNumber(total) }
          );

/** "14 invoices", or the partial wording when the source's record limit cut the rows. */
export const figureCount = (intl: IntlShape, definition: FigureDefinition, rows: FigureRows): string =>
    rows.partial
        ? shownOfTotal(intl, rows.count, rows.total)
        : intl.formatMessage({ id: figureNounId(definition) }, { count: rows.count });

/** A column's label. A list's record and detail columns come labelled with their field name, which reads as words. */
export const columnLabel = ({ field, label }: FigureRowsColumn): string => {
    if (label !== field) {
        return label;
    }
    const words = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

// The words for each direction, by what the sorted field holds; numbers of every kind run lowest to highest.
const SORT_ORDERS: Partial<Record<FigureRowsColumn['type'], [ascending: string, descending: string]>> = {
    date: ['oldest', 'newest'],
    text: ['az', 'za'],
    record: ['az', 'za'],
};

/** "by Due date, oldest first", when the rows are sorted by a field the Figure shows. */
export const figureSort = (intl: IntlShape, definition: FigureDefinition, rows: FigureRows): string | null => {
    const { sort } = definition;
    const column = sort ? rows.columns.find(({ field }) => field === sort.field) : undefined;
    if (!sort || !column) {
        return null;
    }
    const [ascending, descending] = SORT_ORDERS[column.type] ?? ['lowest', 'highest'];
    const order = sort.direction === 'desc' ? descending : ascending;
    return intl.formatMessage({ id: 'ai-chat.figure.sort' }, { label: columnLabel(column), order });
};
